import { CSSProperties, FC, MouseEvent, MutableRefObject, useCallback, useMemo, useState } from 'react'

import { KTIcon } from '../../../../../_metronic/helpers'
import { Table } from '../../../../../components/Table'
import { TableHead } from '../../../../../components/Table/TableHead'
import { TableBody } from '../../../../../components/Table/TableBody'
import { EMatchingPayoutTicketStatus } from '../../../../../types/enums'
import { IsGranted, useAuth } from '../../../auth'
import {
  ColumnVisibility,
  matchingCapabilities,
  MatchingPayoutPaymentTicket,
  MatchingPayoutTicket,
} from '../../core/_models'
import FinishedPayoutPaymentTicketRow from '../payouts-finished/FinishedPayoutPaymentTicketRow'
import { OverpaymentModal } from '../../routes/Overpayments/components/OverpaymentModal/OverpaymentModal'
import { OverpaymentButton } from '../../routes/Overpayments/components/OverpaymentButton/OverpaymentButton'
import LocalAdapterModal from '../../routes/Overpayments/components/LocalAdapterModal/LocalAdapterModal'
import { PAY_VIA_LOCAL_ADAPTER_LABEL } from '../../routes/Overpayments/core/consts'

interface Props {
  ticket: MatchingPayoutTicket
  visibility: ColumnVisibility
  onOverpaymentCreated?: (compensationPayoutToken: string) => void
  /** Переоплата ушла на кассу — выплата изменилась, её нужно перечитать */
  onOverpaymentHandedToCassa?: () => void
  /**
   * Депозиты, по которым переоплата уже заведена в этом сеансе, — кнопка по ним гаснет сразу.
   * Замок принадлежит владельцу таблицы: строка списка размонтирует таблицу при сворачивании,
   * и собственный замок снимался бы вместе с ней.
   */
  overpaymentLock: MutableRefObject<Set<number>>
}

/**
 * Депозиты, сматченные с выплатой, с переоплатой и проведением через локальный адаптер.
 *
 * Один компонент на два места: раскрытая строка активной выплаты и карточка выплаты.
 */
export const PayoutDepositsTable = ({
  ticket,
  visibility,
  onOverpaymentCreated,
  onOverpaymentHandedToCassa,
  overpaymentLock,
}: Props) => {
  const { currentUser } = useAuth()
  const [overpaymentFor, setOverpaymentFor] = useState<MatchingPayoutPaymentTicket | null>(null)
  const [localAdapterFor, setLocalAdapterFor] = useState<number | null>(null)

  const isPayoutTerminal =
    ticket.status === EMatchingPayoutTicketStatus.COMPLETED ||
    ticket.status === EMatchingPayoutTicketStatus.DECLINED
  // CPAYMENT-1726: переоплата открыта и оператору платежей мерчанта — список ролей держим в
  // matchingCapabilities, он обязан совпадать с бэкендом.
  const canUseOverpayment = IsGranted(matchingCapabilities.canCreateOverpayment, currentUser)
  const canOverpay = canUseOverpayment && !isPayoutTerminal

  // Провести зависшую переоплату через локальный адаптер. Право ОТДЕЛЬНОЕ от создания переоплаты:
  // у менеджера мерчантов оно есть, а создания нет, поэтому предикат не опирается на
  // canUseOverpayment (CPAYMENT-1835).
  const canUseLocalAdapter = IsGranted(
    matchingCapabilities.canPayOverpaymentViaLocalAdapter,
    currentUser
  )

  const canPayViaLocalAdapter = useCallback(
    (pt: MatchingPayoutPaymentTicket) =>
      canUseLocalAdapter && !!pt.overpaymentCanPayViaLocalAdapter && !!pt.overpaymentId,
    [canUseLocalAdapter]
  )

  // Ссылка «Уже переоплачивается» доступна, если у депозита есть незавершённая компенсация.
  const hasInProgressOverpayment = useCallback(
    (pt: MatchingPayoutPaymentTicket) =>
      canUseOverpayment && !!pt.overpaymentInProgress && !!pt.overpaymentCompensationToken,
    [canUseOverpayment]
  )

  // Кнопка создания доступна для завершённого депозита активной выплаты без активной переоплаты.
  const canCreateOverpayment = useCallback(
    (pt: MatchingPayoutPaymentTicket) =>
      canOverpay &&
      !pt.overpaymentInProgress &&
      pt.status === 'COMPLETED' &&
      !pt.matchReleased &&
      !!pt.matchingTransactionId,
    [canOverpay]
  )

  const localAdapterButton = useCallback(
    (pt: MatchingPayoutPaymentTicket) =>
      canPayViaLocalAdapter(pt) ? (
        <button
          type='button'
          className='btn btn-sm btn-light-primary'
          onClick={() => setLocalAdapterFor(pt.overpaymentId as number)}
        >
          {PAY_VIA_LOCAL_ADAPTER_LABEL}
        </button>
      ) : null,
    [canPayViaLocalAdapter]
  )

  const renderOverpaymentCell = useCallback(
    (pt: MatchingPayoutPaymentTicket) => {
      // Кнопка живёт РЯДОМ с «В процессе переоплаты», а не вместо неё: зависшая в мэтчинге
      // компенсация — это и есть та самая переоплата, которую нужно провести кассой.
      if (hasInProgressOverpayment(pt)) {
        return (
          <div className='d-flex flex-column align-items-end gap-1'>
            <span className='text-info fw-semibold'>В процессе переоплаты</span>
            <CopyableToken value={pt.overpaymentCompensationToken} />
            {localAdapterButton(pt)}
          </div>
        )
      }

      // Роль без права создавать переоплату (менеджер мерчантов) до ветки выше не доходит —
      // для неё кнопка рисуется здесь.
      const localAdapter = localAdapterButton(pt)
      if (localAdapter !== null) {
        return localAdapter
      }

      if (!canCreateOverpayment(pt)) return null

      const alreadyCreated =
        pt.matchingTransactionId != null &&
        overpaymentLock.current.has(pt.matchingTransactionId)

      return (
        <OverpaymentButton
          history={pt.overpaymentHistory}
          disabled={alreadyCreated}
          onClick={() => setOverpaymentFor(pt)}
        />
      )
    },
    [hasInProgressOverpayment, canCreateOverpayment, localAdapterButton, overpaymentLock]
  )

  // Заголовок колонки действий показываем только если хотя бы у одного депозита
  // есть кнопка или ссылка (иначе пустой столбец без шапки не нужен).
  const hasAnyOverpaymentCell = useMemo(
    () =>
      (ticket.paymentTicketList ?? []).some(
        (pt) =>
          hasInProgressOverpayment(pt) || canCreateOverpayment(pt) || canPayViaLocalAdapter(pt)
      ),
    [
      ticket.paymentTicketList,
      hasInProgressOverpayment,
      canCreateOverpayment,
      canPayViaLocalAdapter,
    ]
  )

  return (
    <>
      <Table>
        <TableHead>
          <tr>
            <th className='min-w-120px ps-8'>
              <small>ID транзакции</small>
            </th>
            {visibility.createdDate && (
              <th className='min-w-100px'>
                <small>Дата создания</small>
              </th>
            )}
            {visibility.completionDate && (
              <th className='min-w-100px'>
                <small>Дата закрытия</small>
              </th>
            )}
            {visibility.amount && (
              <th className='min-w-160px'>
                <small>Сумма (запрос / факт)</small>
              </th>
            )}
            {visibility.user && (
              <th className='min-w-100px'>
                <small>Клиент</small>
              </th>
            )}
            {visibility.merchantId && (
              <th className='min-w-120px'>
                <small>ID мерчанта</small>
              </th>
            )}
            {visibility.merchant && (
              <th className='min-w-120px'>
                <small>Мерчант</small>
              </th>
            )}
            {visibility.status && (
              <th className='min-w-140px'>
                <small>Статус</small>
              </th>
            )}
            {visibility.file && (
              <th className='min-w-120px text-center align-middle'>
                <small>Чек</small>
              </th>
            )}
            {visibility.authorId && (
              <th className='min-w-100px text-center align-middle'>
                <small>Author ID</small>
              </th>
            )}
            {hasAnyOverpaymentCell && (
              <th className='min-w-120px text-end align-middle'>
                <small>Переоплата</small>
              </th>
            )}
          </tr>
        </TableHead>
        <TableBody>
          {ticket.paymentTicketList.map((pt, idx) => (
            <FinishedPayoutPaymentTicketRow
              key={pt.merchantTransactionId || idx}
              ticket={pt}
              visibility={visibility}
              actionSlot={renderOverpaymentCell(pt)}
              showActionColumn={hasAnyOverpaymentCell}
            />
          ))}
        </TableBody>
      </Table>
      <LocalAdapterModal
        show={localAdapterFor !== null}
        overpaymentId={localAdapterFor}
        onClose={() => setLocalAdapterFor(null)}
        onDone={() => onOverpaymentHandedToCassa?.()}
      />
      {overpaymentFor && (
        <OverpaymentModal
          show={overpaymentFor !== null}
          onClose={() => setOverpaymentFor(null)}
          onCreated={(record) => {
            if (overpaymentFor.matchingTransactionId != null) {
              overpaymentLock.current.add(overpaymentFor.matchingTransactionId)
            }
            setOverpaymentFor(null)
            onOverpaymentCreated?.(record.compensationPayoutTicket.token)
          }}
          context={{
            matchingTransactionId: overpaymentFor.matchingTransactionId as number,
            amount: overpaymentFor.amount ?? 0,
            currency: 'RUB',
            paymentMethod: ticket.paymentMethod,
            requisite: ticket.matchingTicketRequisite ?? '',
            recipient: {
              externalId: ticket.externalUserId,
              merchantToken: ticket.merchant,
            },
            culprit: {
              externalId: overpaymentFor.externalUserId ?? '',
              merchantToken: overpaymentFor.merchant,
            },
            history: overpaymentFor.overpaymentHistory,
          }}
        />
      )}
    </>
  )
}

// Мелкий моноширинный токен, кликабельный для копирования. При наведении
// появляется обводка с иконкой — как CopyableCell в аудит-логах интеграции.
const CopyableToken: FC<{ value: string | null | undefined }> = ({ value }) => {
  const [hovered, setHovered] = useState(false)
  const [copied, setCopied] = useState(false)

  if (!value) return null

  const handleCopy = (e: MouseEvent) => {
    e.stopPropagation()
    navigator.clipboard.writeText(value).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 1200)
    })
  }

  const textStyle: CSSProperties = {
    fontFamily: 'monospace',
    fontSize: '0.75rem',
    whiteSpace: 'nowrap',
  }

  return (
    <span
      style={{
        position: 'relative',
        display: 'inline-block',
        whiteSpace: 'nowrap',
        cursor: 'pointer',
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={handleCopy}
    >
      <span className='text-muted' style={textStyle}>
        {value}
      </span>
      {hovered && (
        <span
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
          onClick={handleCopy}
          style={{
            position: 'absolute',
            top: '-4px',
            right: '-8px',
            bottom: '-4px',
            width: 'max-content',
            border: '1px solid var(--bs-border-color)',
            borderRadius: '5px',
            boxShadow: '0 2px 10px rgba(0,0,0,0.13)',
            background: 'var(--bs-body-bg)',
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            paddingLeft: '8px',
            paddingRight: '8px',
            zIndex: 10,
          }}
        >
          <span style={textStyle}>{value}</span>
          <KTIcon
            iconName={copied ? 'check-circle' : 'copy'}
            className={`fs-5 ${copied ? 'text-success' : 'text-gray-500'}`}
          />
        </span>
      )}
    </span>
  )
}

export default PayoutDepositsTable
